import * as dayjs from 'dayjs';
import { groupBy as _groupBy } from 'lodash';
// deep import: keep the icon package's component code out of the store import chain
import { BringgFontIcons } from '@bringg/bringg-icons/dist/types/icon-types';

import DateService, { inTimezone } from '../../services/date-service';
import { RescheduleOption } from '../../types/shared-location.types';

export interface FlowMessage {
	key: string;
	args?: Record<string, unknown>;
}

export interface FlowReply {
	id: string;
	// Exactly one of labelKey (translated) / label (raw value, e.g. a formatted window) is set
	labelKey?: string;
	label?: string;
	icon?: BringgFontIcons;
	nextStepId: string;
	nextArgs?: Record<string, unknown>;
	option?: RescheduleOption;
	ownRow?: boolean;
}

export interface FlowStep {
	id: string;
	messages: FlowMessage[];
	replies: FlowReply[];
}

export type RescheduleFlow = Record<string, FlowStep>;

export interface RescheduleContext {
	merchantName: string;
	orderId: string;
	deliveryWindowStart: string;
	deliveryWindowEnd: string;
	timezone?: string;
	maxDisplayedResults?: number;
	optionsUrl: string;
	updateUrl: string;
	onExit?: () => void;
}

export const START_STEP_ID = 'start';
export const CONFIRMED_STEP_ID = 'confirmed';
export const SAME_DAY_EMPTY_STEP_ID = 'same-day-empty';
// Handled directly by the store, so they live outside the flow tree.
export const TRY_AGAIN_REPLY_ID = 'try-again';
export const EXIT_REPLY_ID = 'exit';

export const exitReply: FlowReply = {
	id: EXIT_REPLY_ID,
	labelKey: 'RESCHEDULE.BACK_TO_DELIVERY',
	icon: BringgFontIcons.ArrowLeft,
	nextStepId: null,
	ownRow: true
};

const windowsStepId = (dayKey: string): string => `windows:${dayKey}`;

const dayKeyOf = (iso: string, tz?: string): string => inTimezone(iso, tz).format('YYYY-MM-DD');

const formatWindowTime = (startIso: string, endIso: string, tz?: string): string =>
	`${DateService.formatTime(startIso, tz)} - ${DateService.formatTime(endIso, tz)}`;

const byStartTimeAsc = (a: RescheduleOption, b: RescheduleOption): number =>
	dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf();

export const createRescheduleFlow = (context: RescheduleContext, options: RescheduleOption[]): RescheduleFlow => {
	const { timezone: tz } = context;
	const sorted = [...options].sort(byStartTimeAsc);
	// Keys are 'YYYY-MM-DD', never integer-like, so the object keeps the ascending insertion order.
	const dayGroups = _groupBy(sorted, ({ start_time }) => dayKeyOf(start_time, tz));
	const currentDayKey = dayKeyOf(context.deliveryWindowStart, tz);
	const hasSameDayWindows = Boolean(dayGroups[currentDayKey]);
	const firstAvailable = context.maxDisplayedResults > 0 ? sorted.slice(0, context.maxDisplayedResults) : sorted;

	const confirmReply = (option: RescheduleOption, withDate: boolean): FlowReply => ({
		id: `window:${option.id}`,
		label: withDate
			? DateService.formatWindow(option.start_time, option.end_time, tz)
			: formatWindowTime(option.start_time, option.end_time, tz),
		nextStepId: CONFIRMED_STEP_ID,
		// the delivery_window arg for the CONFIRMED message is supplied by the store after a successful submit
		option
	});

	const dateReplies: FlowReply[] = Object.entries(dayGroups).map(([dayKey, dayOptions]) => ({
		id: `date:${dayKey}`,
		label: DateService.formatLocalizedDay(dayOptions[0].start_time, tz),
		nextStepId: windowsStepId(dayKey)
	}));

	const backToStart: FlowReply = {
		id: 'back-to-start',
		labelKey: 'RESCHEDULE.BACK_TO_START',
		icon: BringgFontIcons.ArrowLeft,
		nextStepId: START_STEP_ID,
		ownRow: true
	};

	const flow: RescheduleFlow = {
		[START_STEP_ID]: {
			id: START_STEP_ID,
			messages: [],
			replies: [
				{ id: 'first-available', labelKey: 'RESCHEDULE.FIRST_AVAILABLE', nextStepId: 'first-available' },
				{
					id: 'same-day',
					labelKey: 'RESCHEDULE.SAME_DAY',
					nextStepId: hasSameDayWindows ? windowsStepId(currentDayKey) : SAME_DAY_EMPTY_STEP_ID
				},
				{ id: 'pick-date', labelKey: 'RESCHEDULE.PICK_A_DATE', nextStepId: 'pick-date' }
			]
		},
		'first-available': {
			id: 'first-available',
			messages: [{ key: 'RESCHEDULE.NEAREST_WINDOWS' }],
			replies: [...firstAvailable.map(option => confirmReply(option, true)), backToStart]
		},
		'pick-date': {
			id: 'pick-date',
			messages: [{ key: 'RESCHEDULE.WHICH_DATE' }],
			replies: [...dateReplies, backToStart]
		},
		[SAME_DAY_EMPTY_STEP_ID]: {
			id: SAME_DAY_EMPTY_STEP_ID,
			messages: [{ key: 'RESCHEDULE.NO_WINDOWS' }],
			replies: [...dateReplies, backToStart]
		},
		[CONFIRMED_STEP_ID]: {
			id: CONFIRMED_STEP_ID,
			messages: [{ key: 'RESCHEDULE.CONFIRMED' }],
			replies: [exitReply]
		}
	};

	for (const [dayKey, dayOptions] of Object.entries(dayGroups)) {
		flow[windowsStepId(dayKey)] = {
			id: windowsStepId(dayKey),
			messages: [
				{
					key: 'RESCHEDULE.WINDOWS_FOR_DATE',
					args: { date: DateService.formatLocalizedDay(dayOptions[0].start_time, tz) }
				}
			],
			replies: [...dayOptions.map(option => confirmReply(option, false)), backToStart]
		};
	}

	return flow;
};
